import * as React from 'react';
import { useMemo } from 'react';
import { FieldError } from 'react-hook-form';
import Select, {
	SelectValue,
} from '../../../../../Components/Base/Select/Select';
import {
	PaginationOrderHow,
	TermItem,
	useTermItemsQuery,
} from '../../../../../graphql/graphql';
import useTranslation from '../../../../../Hooks/useTranslation';
import useSelectValueGuard from '../../../Hooks/useSelectValueGuard';
import Loading from '../../../../../Components/Loading';

export interface CSSZInsuranceAdministratorSelectProps {
	value?: SelectValue;
	onChange?: (value: SelectValue, termItem?: TermItem) => void;
	error?: FieldError;
	placeholder?: string;
	isClearable?: boolean;
	disabled?: boolean;
	portal?: boolean;
	hasError?: boolean;
	style?: React.CSSProperties;
}

type Props = CSSZInsuranceAdministratorSelectProps;

const TERM_TYPE = 'cssz_spravce_pojisteni';

const formatLabel = (termItem: TermItem) => {
	const value = termItem.value?.trim();
	const name = termItem.name?.trim();

	if (value && name) {
		return `${value} - ${name}`;
	}

	return name || value || termItem.id;
};

const CSSZInsuranceAdministratorSelect: React.FC<Props> = props => {
	const { value, onChange, placeholder, isClearable = true } = props;
	const { t } = useTranslation();

	const { data, isLoading } = useTermItemsQuery({
		termType: TERM_TYPE,
		take: 999,
		pagination: {
			order: {
				key: 'term_items.value',
				how: PaginationOrderHow.Asc,
			},
		},
	});

	const termItems = data?.termItems?.data || [];

	const options: SelectValue[] = useMemo(() => {
		return termItems
			.filter((termItem): termItem is TermItem => Boolean(termItem))
			.map(termItem => ({
				value: termItem.id,
				label: formatLabel(termItem),
			}));
	}, [termItems]);

	useSelectValueGuard({ options, value, onChange });

	const handleChange = (selectedValue?: SelectValue | null) => {
		if (!selectedValue) {
			onChange?.({ value: '', label: '' }, undefined);
			return;
		}

		const termItem = termItems.find(item => item?.id === selectedValue.value);
		onChange?.(selectedValue, termItem);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<Select
			{...props}
			options={options}
			value={value || options[0]}
			onChange={handleChange}
			placeholder={placeholder || t('base.shared.pick')}
			isClearable={isClearable}
		/>
	);
};

export default CSSZInsuranceAdministratorSelect;
